import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@platform/ui'
import { Building2, HelpCircle, Layers, TrendingUp, X } from 'lucide-react'
import { useRouter } from '@/lib/mocks/router'
import { registrar } from '@/lib/demo/telemetry'
import { diasRestantes } from '@/lib/demo/lead-ref'
import { ActivarPlataforma } from './ActivarPlataforma'
import { useRecorrido } from './DemoTour'
import { paso } from './DemoTour'

type Perfil = 'inversor' | 'emisor'

/**
 * Conmutador flotante entre las dos caras de la plataforma.
 *
 * POR QUÉ EXISTE, que no es obvio: quien pide una demostración de una plataforma
 * de tokenización no quiere invertir — quiere VENDER la plataforma. Entra
 * mirándola como inversor porque es lo que se le presenta, pero la decisión de
 * compra la toma cuando se pone el sombrero de dueño y ve su propio panel: sus
 * proyectos, sus inversores, su recaudación.
 *
 * Por eso `issuer_panel_opened` pesa casi tanto como simular una compra. No mide
 * curiosidad: mide que dejó de evaluar la inversión y empezó a evaluar el
 * producto.
 *
 * Flotante y siempre presente a propósito. Si estuviera enterrado en un menú, la
 * mitad de los prospectos no lo encontraría y perderíamos justo la señal que más
 * dice.
 */
export function DemoSwitcher({ perfilActual = 'inversor' }: { perfilActual?: Perfil }) {
  const router = useRouter()
  const [abierto, setAbierto] = useState(false)
  const { abrir: abrirRecorrido } = useRecorrido()
  const dias = diasRestantes()

  function cambiarA(perfil: Perfil) {
    if (perfil === 'emisor') {
      // La señal se emite ANTES de navegar: si el prospecto cierra la pestaña
      // nada más entrar, el beacon ya salió (`sendBeacon` sobrevive al cierre).
      registrar('issuer_panel_opened')
      router.push('/dashboard/admin')
    } else {
      router.push('/dashboard/user')
    }
    setAbierto(false)
  }

  return (
    <>
      {/* Botón flotante. Abajo a la derecha, por encima de todo. */}
      <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2" {...paso('conmutador')}>
        {/*
          La llamada a la acción va ARRIBA del conmutador y con menos peso
          visual: quien todavía está explorando busca el conmutador, y quien ya
          decidió encuentra esto sin tener que ir a buscarlo a un menú.
        */}
        <ActivarPlataforma />
        <Button
          onClick={() => setAbierto(true)}
          size="lg"
          className="shadow-lg shadow-primary/25 gap-2 rounded-full"
          aria-label="Cambiar de perfil o pedir ayuda"
        >
          <Layers className="h-4 w-4" />
          <span className="hidden sm:inline">
            {perfilActual === 'inversor' ? 'Ver como emisor' : 'Ver como inversor'}
          </span>
          <span className="sm:hidden">Cambiar</span>
        </Button>
      </div>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Desde dónde querés mirar?</DialogTitle>
            <DialogDescription>
              La misma plataforma tiene dos caras. Podés pasar de una a otra cuando quieras, sin
              perder nada de lo que hiciste.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 pt-2">
            <button
              type="button"
              onClick={() => cambiarA('inversor')}
              className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-accent ${
                perfilActual === 'inversor' ? 'border-primary bg-accent/50' : 'border-border'
              }`}
            >
              <TrendingUp className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>
                <span className="block font-medium">Inversor</span>
                <span className="block text-sm text-muted-foreground">
                  Explorá activos, invertí, cobrá rendimientos y retirá.
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => cambiarA('emisor')}
              className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-accent ${
                perfilActual === 'emisor' ? 'border-primary bg-accent/50' : 'border-border'
              }`}
            >
              <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>
                <span className="block font-medium">Emisor de la plataforma</span>
                <span className="block text-sm text-muted-foreground">
                  El panel del dueño: tus proyectos, tus inversores y tu recaudación.
                </span>
              </span>
            </button>
          </div>

          <div className="flex items-center gap-2 border-t pt-4">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => {
                setAbierto(false)
                abrirRecorrido()
              }}
            >
              <HelpCircle className="h-4 w-4" />
              Repetir el recorrido guiado
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto gap-2"
              onClick={() => setAbierto(false)}
            >
              <X className="h-4 w-4" />
              Cerrar
            </Button>
          </div>

          {/*
            Lo único que queda del antiguo cartel superior, y por un motivo
            distinto: la vigencia del acceso es información útil para quien
            entra, no una advertencia sobre el entorno. Va aquí dentro y no en
            una franja fija — se consulta cuando importa y no compite con el
            producto por la atención.
          */}
          {dias !== null ? (
            <p className="text-center text-xs text-muted-foreground">
              {dias === 0
                ? 'Tu acceso vence hoy.'
                : dias === 1
                  ? 'Tu acceso vence mañana.'
                  : `Tu acceso sigue activo ${dias} días más.`}
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  )
}
